/**
 * Modal System - Moon Moon Agency
 * Generic data-modal system: slide-right on desktop, slide-up on mobile
 * Keyboard accessible: focus trap, initial focus, trigger restoration
 *
 * Usage:
 *   Trigger:  <button data-modal-open="my-modal">Open</button>
 *   Modal:    <div class="mm-modal" data-modal="my-modal">...</div>
 *   Close:    <button data-modal-close>X</button> (inside modal)
 */

const ModalManager = (() => {
    'use strict';

    // ==========================================================================
    // Configuration
    // ==========================================================================
    const ACTIVE_CLASS    = 'mm-active';
    const BODY_LOCK_CLASS = 'mm-modal-open';

    const FOCUSABLE_SELECTOR = [
        'summary',
        'a[href]',
        'button:not([disabled])',
        'input:not([type="hidden"]):not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex^="-"])',
        '[draggable]',
        'area',
        'object',
        'iframe',
    ].join(', ');

    // ==========================================================================
    // State
    // ==========================================================================
    let overlay        = null;
    let activeModal    = null;
    let triggerElement = null;

    // ==========================================================================
    // Overlay
    // ==========================================================================
    function getOverlay() {
        return overlay;
    }

    function createOverlay() {
        overlay = document.createElement('div');
        overlay.classList.add('mm-modal-overlay');
        overlay.addEventListener('click', close);
        document.body.appendChild(overlay);
    }

    // ==========================================================================
    // Focus helpers
    // ==========================================================================
    function getFocusableElements(container) {
        return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
            .filter((el) => !el.closest('[hidden]') && !el.closest('[inert]'));
    }

    function focusTrapKeydown(e) {
        if (e.key !== 'Tab' || !activeModal) return;

        const focusable = getFocusableElements(activeModal);
        if (!focusable.length) return;

        const first = focusable[0];
        const last  = focusable[focusable.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }

    // ==========================================================================
    // Open
    // ==========================================================================
    function open(modalId, trigger) {
        const modal = document.querySelector(`[data-modal="${modalId}"]`);
        if (!modal) return;

        if (activeModal) close();

        triggerElement = trigger || null;
        activeModal    = modal;

        // ARIA — aussi défini statiquement en Liquid, ici en fallback
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');

        modal.removeAttribute('inert');
        getOverlay().classList.add(ACTIVE_CLASS);
        modal.classList.add(ACTIVE_CLASS);
        document.body.classList.add(BODY_LOCK_CLASS);
        window.LenisManager?.stop();

        modal.addEventListener('click', handleCloseClick);
        document.addEventListener('keydown', handleEscape);
        modal.addEventListener('keydown', focusTrapKeydown);

        // Focus initial : bouton fermer en priorité, sinon premier élément focusable
        const closeBtn       = modal.querySelector('[data-modal-close]');
        const firstFocusable = getFocusableElements(modal)[0];
        (closeBtn || firstFocusable || modal)?.focus();

        document.dispatchEvent(
            new CustomEvent('modal:opened', { detail: { id: modalId } })
        );
    }

    // ==========================================================================
    // Close
    // ==========================================================================
    function close() {
        if (!activeModal) return;

        const closedId     = activeModal.getAttribute('data-modal');
        const savedTrigger = triggerElement;

        activeModal.classList.remove(ACTIVE_CLASS);
        activeModal.setAttribute('inert', '');
        activeModal.removeEventListener('click', handleCloseClick);
        activeModal.removeEventListener('keydown', focusTrapKeydown);
        getOverlay().classList.remove(ACTIVE_CLASS);
        document.body.classList.remove(BODY_LOCK_CLASS);
        window.LenisManager?.resume();
        document.removeEventListener('keydown', handleEscape);

        activeModal    = null;
        triggerElement = null;

        // Restaurer le focus sur l'élément déclencheur
        if (savedTrigger && typeof savedTrigger.focus === 'function') {
            savedTrigger.focus();
        }

        document.dispatchEvent(
            new CustomEvent('modal:closed', { detail: { id: closedId } })
        );
    }

    // ==========================================================================
    // Event handlers
    // ==========================================================================
    function handleCloseClick(e) {
        if (e.target.closest('[data-modal-close]')) close();
    }

    function handleEscape(e) {
        if (e.key === 'Escape') close();
    }

    // ==========================================================================
    // Init
    // ==========================================================================
    function init() {
        // Créer l'overlay au chargement pour que la transition CSS fonctionne dès le premier clic
        createOverlay();

        // Rendre toutes les modals inertes au chargement
        document.querySelectorAll('[data-modal]').forEach((modal) => {
            modal.setAttribute('inert', '');
        });

        document.addEventListener('click', (e) => {
            const trigger = e.target.closest('[data-modal-open]');
            if (trigger) {
                e.preventDefault();
                open(trigger.getAttribute('data-modal-open'), trigger);
            }
        });
    }

    document.readyState === 'loading'
        ? document.addEventListener('DOMContentLoaded', init)
        : init();

    // ==========================================================================
    // Public API
    // ==========================================================================
    return { open, close };
})();
